// @ts-nocheck
/**
 * Set Division Places.
 *
 * The division's standings with the computed columns, a Place box per team and
 * a Generate action. When the configured chain fully separates every team,
 * Generate fills the places. When it does not, the tied group is named — which
 * teams, at which place, and which criterion ran out — and the director sets
 * those places by hand. A place set by hand is marked manual and is never
 * overwritten by a later Generate.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, Wand2, RotateCcw, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const call = async (body: Record<string, unknown>) => {
  const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', { body });
  if (error) {
    let message = error.message || 'Request failed';
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message = parsed?.error?.message || message;
      }
    } catch { /* keep the original message */ }
    return { ok: false, message };
  }
  if (!res?.ok) return { ok: false, message: res?.error?.message ?? 'Request failed' };
  return { ok: true, data: res.data };
};

interface Props {
  eventId: string;
  division: string;
}

const DivisionPlaces: React.FC<Props> = ({ eventId, division }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<any>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const load = useCallback(async (action = 'standings_data') => {
    setLoading(true);
    const res = await call({ action, event_id: eventId, division });
    setLoading(false);
    if (!res.ok) { toast.error(res.message); return null; }
    setData(res.data);
    setDraft({});
    return res.data;
  }, [eventId, division]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    const d = await load('places_generate');
    setBusy(false);
    if (!d) return;
    if (d.refused_groups?.length) {
      toast.warning('Some places could not be generated — tied teams are listed below.');
    } else {
      toast.success(`${d.generated_count} place${d.generated_count === 1 ? '' : 's'} generated.`);
    }
  };

  const savePlace = async (registrationId: string, value: string) => {
    setBusy(true);
    const res = await call({
      action: 'places_set', event_id: eventId, division,
      registration_id: registrationId, place: value,
    });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return; }
    setData(res.data);
    setDraft({});
    toast.success('Place set by hand.');
  };

  const clearPlace = async (registrationId?: string) => {
    setBusy(true);
    const res = await call({
      action: 'places_clear', event_id: eventId, division,
      ...(registrationId ? { registration_id: registrationId } : {}),
    });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return; }
    setData(res.data);
    setDraft({});
  };

  if (loading && !data) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading standings…
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const unresolved = data?.unresolved ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Trophy className="w-4 h-4" /> Division places — {division}
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            Ranked by the division tie-breaker chain. Ties are never broken by team name.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" onClick={generate} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Generate places
          </Button>
          <Button size="sm" variant="outline" onClick={() => clearPlace()} disabled={busy}>
            <RotateCcw className="w-4 h-4 mr-1" /> Clear all
          </Button>
        </div>
      </div>

      {data?.max_point_differential ? (
        <p className="text-xs text-muted-foreground">
          Point differential is capped at {data.max_point_differential} per game for this division.
        </p>
      ) : null}

      {unresolved.map((u: any) => (
        <div key={u.place} className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{u.message}</span>
        </div>
      ))}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Team</th>
              <th className="text-left px-3 py-2">Pool</th>
              <th className="text-center px-2 py-2">Pool place</th>
              <th className="text-center px-2 py-2">W-L-T</th>
              <th className="text-center px-2 py-2">Win %</th>
              <th className="text-center px-2 py-2">PF</th>
              <th className="text-center px-2 py-2">PA</th>
              <th className="text-center px-2 py-2">Diff</th>
              <th className="text-center px-2 py-2">Pts</th>
              <th className="text-center px-2 py-2 w-36">Place</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r: any) => (
              <tr key={r.registration_id} className={r.tied ? 'bg-amber-50/60' : ''}>
                <td className="px-3 py-2 font-medium">
                  {r.team_name}
                  {r.tied && <Badge variant="outline" className="ml-2 text-[10px]">Tied</Badge>}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.pool_name ?? '—'}</td>
                <td className="px-2 py-2 text-center">
                  {r.pool_rank ?? '—'}{r.pool_rank_tied ? '*' : ''}
                </td>
                <td className="px-2 py-2 text-center">{r.wins}-{r.losses}{r.ties ? `-${r.ties}` : ''}</td>
                <td className="px-2 py-2 text-center">{(r.win_pct * 100).toFixed(0)}%</td>
                <td className="px-2 py-2 text-center">{r.points_for}</td>
                <td className="px-2 py-2 text-center">{r.points_against}</td>
                <td className="px-2 py-2 text-center">{r.diff > 0 ? `+${r.diff}` : r.diff}</td>
                <td className="px-2 py-2 text-center">{r.standing_points}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <Input
                      className="h-8 w-16 text-center"
                      inputMode="numeric"
                      value={draft[r.registration_id] ?? (r.place ?? '')}
                      onChange={(e) => setDraft((d) => ({ ...d, [r.registration_id]: e.target.value }))}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v === '' || String(r.place ?? '') === v) return;
                        savePlace(r.registration_id, v);
                      }}
                      disabled={busy}
                    />
                    {r.place_is_manual && (
                      <Badge variant="secondary" className="text-[10px]">Manual</Badge>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No pooled teams in this division yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        A place typed here is kept as manual and survives every recompute. Pool places marked * are themselves tied.
      </p>
    </div>
  );
};

export default DivisionPlaces;
